import React, { useState } from 'react';
import { FileSpreadsheet, Plus, Trash2, UserPlus, Users } from 'lucide-react';
import { portalAdminService } from '../../services/api';

const fa = (n: number) => n.toLocaleString('fa-IR');
const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

const HEALTH: Record<string, { label: string; color: string }> = {
  GREEN: { label: 'سبز', color: '#0ca30c' },
  YELLOW: { label: 'زرد', color: '#fab219' },
  RED: { label: 'قرمز', color: '#d03b3b' },
};

interface Props {
  programId: string;
  cohorts: any[];
  mentors: any[];
  onReload: () => Promise<void>;
  onError: (err: any, fallback: string) => void;
  onFlash: (message: string) => void;
}

/** گروه‌ها، منتور هر گروه و افزودن شرکت‌کننده */
export const PortalCohortsTab: React.FC<Props> = ({
  programId,
  cohorts,
  mentors,
  onReload,
  onError,
  onFlash,
}) => {
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState('');
  const [roster, setRoster] = useState<any[]>([]);
  const [phones, setPhones] = useState('');
  const [importNote, setImportNote] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (err: any) {
      onError(err, fallback);
    } finally {
      setBusy(false);
    }
  };

  const openCohort = async (cohortId: string) => {
    if (openId === cohortId) {
      setOpenId('');
      return;
    }
    setOpenId(cohortId);
    try {
      setRoster(await portalAdminService.listParticipants({ cohortId }));
    } catch (err: any) {
      onError(err, 'دریافت اعضا انجام نشد.');
    }
  };

  /**
   * فایل اکسل را سرور می‌خواند و فقط شماره‌ها را برمی‌گرداند؛ اینجا داخل همان
   * کادر متنی می‌نشینند تا ادمین قبل از افزودن ببیندشان. افزودن مستقیم از فایل
   * یعنی یک ستون اشتباه، بی‌سروصدا چند نفر را وارد گروه کند.
   */
  const importFromFile = (file: File) =>
    run(async () => {
      setImportNote('');
      const result = await portalAdminService.extractPhonesFromFile(file);

      if (!result.phones.length) {
        setImportNote(
          result.scannedCells
            ? 'در این فایل شمارهٔ موبایل معتبری پیدا نشد. ' +
                fa(result.scannedCells) +
                ' سلول بررسی شد.'
            : 'فایل خالی بود.',
        );
        return;
      }

      /* شماره‌های موجود در کادر حفظ می‌شوند تا چند فایل پشت هم قابل افزودن باشد */
      const existing = phones
        .split(/[\s,;\n]+/)
        .map((x) => x.trim())
        .filter(Boolean);
      /* Array.from به‌جای spread — تنظیم target این پروژه اجازهٔ پیمایش Set را نمی‌دهد */
      const merged = Array.from(new Set(existing.concat(result.phones)));
      setPhones(merged.join('\n'));

      const parts = [fa(result.phones.length) + ' شماره از فایل خوانده شد'];
      if (result.duplicates) parts.push(fa(result.duplicates) + ' تکراری کنار گذاشته شد');
      if (result.invalid.length) {
        parts.push('نامعتبر: ' + result.invalid.slice(0, 5).join('، '));
      }
      setImportNote(parts.join(' · ') + '. قبل از افزودن بررسی کنید.');
    }, 'خواندن فایل انجام نشد.');

  const addPeople = (cohortId: string) =>
    run(async () => {
      const list = phones
        .split(/[\s,;\n]+/)
        .map((x) => x.trim())
        .filter(Boolean);
      if (!list.length) {
        onError(null, 'حداقل یک شماره وارد کنید.');
        return;
      }
      const result = await portalAdminService.addParticipants(programId, cohortId, {
        phones: list,
      });
      setPhones('');
      setImportNote('');
      setRoster(await portalAdminService.listParticipants({ cohortId }));
      await onReload();

      /* شماره‌های بدون حساب باید دیده شوند، نه اینکه بی‌صدا رد شوند */
      const missing = result.notFound?.length
        ? ' — بدون حساب: ' + result.notFound.join('، ')
        : '';
      onFlash(fa(result.added) + ' نفر اضافه شد' + missing);
    }, 'افزودن اعضا انجام نشد.');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-gray-600">{fa(cohorts.length)} گروه</p>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            run(async () => {
              await portalAdminService.createCohort(programId, {});
              await onReload();
            }, 'ساخت گروه انجام نشد.')
          }
          className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white"
        >
          <Plus className="h-4 w-4" />
          گروه جدید
        </button>
      </div>

      {cohorts.map((cohort) => {
        const open = openId === cohort.id;
        return (
          <div key={cohort.id} className="rounded-xl border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center gap-2 p-3">
              <button
                type="button"
                onClick={() => openCohort(cohort.id)}
                className="flex min-w-0 flex-1 items-center gap-2 text-right"
              >
                <Users className="h-4 w-4 shrink-0 text-gray-400" />
                <span className="min-w-0 flex-1 truncate text-sm font-bold text-gray-800">
                  {cohort.name}
                </span>
              </button>

              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-600">
                {fa(cohort.participants ?? cohort._count?.participants ?? 0)} از {fa(cohort.capacity)}
              </span>

              {cohort.health ? (
                <span className="flex items-center gap-1.5">
                  {(['GREEN', 'YELLOW', 'RED'] as const).map((key) =>
                    cohort.health[key] ? (
                      <span
                        key={key}
                        className="rounded-full px-1.5 py-0.5 text-[10.5px] font-black"
                        style={{
                          backgroundColor: HEALTH[key].color + '18',
                          color: HEALTH[key].color,
                        }}
                      >
                        {fa(cohort.health[key])}
                      </span>
                    ) : null,
                  )}
                </span>
              ) : null}

              <select
                value={cohort.mentor?.id ?? cohort.mentorId ?? ''}
                onChange={(e) =>
                  run(async () => {
                    await portalAdminService.updateCohort(cohort.id, { mentorId: e.target.value });
                    await onReload();
                    onFlash('منتور گروه تغییر کرد.');
                  }, 'تغییر منتور انجام نشد.')
                }
                className="w-44 rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
              >
                <option value="">— بدون منتور —</option>
                {mentors.map((m) => (
                  <option key={m.id} value={m.id}>
                    {[m.firstName, m.lastName].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </div>

            {open ? (
              <div className="space-y-3 border-t border-gray-100 p-3">
                <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-3">
                  <p className="mb-1.5 text-xs font-bold text-gray-700">
                    افزودن شرکت‌کننده با شمارهٔ موبایل
                  </p>
                  <p className="mb-2 text-[11px] font-bold text-gray-500">
                    فقط کسانی اضافه می‌شوند که از قبل در سایت حساب دارند؛ بقیه گزارش می‌شوند.
                  </p>
                  <textarea
                    rows={3}
                    value={phones}
                    onChange={(e) => setPhones(e.target.value)}
                    placeholder="۰۹۱۲۱۲۳۴۵۶۷، ۰۹۳۵۱۱۱۲۲۳۳ — یا هر خط یک شماره"
                    className={inputClass}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => addPeople(cohort.id)}
                      className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
                    >
                      <UserPlus className="h-4 w-4" />
                      افزودن به این گروه
                    </button>

                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-bold text-gray-700 transition hover:border-blue-400 hover:text-blue-700 disabled:opacity-50"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      خواندن از فایل اکسل
                    </button>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        /* ورودی پاک می‌شود تا انتخاب دوبارهٔ همان فایل هم رویداد بدهد */
                        e.target.value = '';
                        if (file) void importFromFile(file);
                      }}
                    />

                    <span className="text-[11px] font-bold text-gray-400">
                      ستون‌بندی مهم نیست؛ هر سلولی که شمارهٔ موبایل باشد خوانده می‌شود.
                    </span>
                  </div>

                  {importNote ? (
                    <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11.5px] font-bold text-amber-900">
                      {importNote}
                    </p>
                  ) : null}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[620px] text-right text-sm">
                    <thead className="text-[11px] font-bold text-gray-400">
                      <tr>
                        <th className="px-2 pb-2">نام</th>
                        <th className="px-2 pb-2">موبایل</th>
                        <th className="px-2 pb-2">وضعیت</th>
                        <th className="px-2 pb-2">پیشرفت</th>
                        <th className="px-2 pb-2">عقب‌افتاده</th>
                        <th className="px-2 pb-2">انتقال</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {roster.map((row) => (
                        <tr key={row.id}>
                          <td className="px-2 py-2 text-xs font-bold text-gray-800">
                            {[row.user.firstName, row.user.lastName].filter(Boolean).join(' ') || '—'}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-500">{row.user.phone}</td>
                          <td className="px-2 py-2">
                            <span
                              className="rounded-full px-2 py-0.5 text-[10.5px] font-black"
                              style={{
                                backgroundColor: HEALTH[row.health].color + '18',
                                color: HEALTH[row.health].color,
                              }}
                            >
                              {HEALTH[row.health].label}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-xs font-bold tabular-nums text-gray-600">
                            {fa(row.progressPercent)}٪
                          </td>
                          <td className="px-2 py-2 text-xs font-bold tabular-nums text-red-600">
                            {row.overdueCount ? fa(row.overdueCount) : '—'}
                          </td>
                          <td className="px-2 py-2">
                            <select
                              value={row.cohort.id}
                              onChange={(e) =>
                                run(async () => {
                                  await portalAdminService.moveParticipant(row.id, {
                                    cohortId: e.target.value,
                                  });
                                  setRoster(
                                    await portalAdminService.listParticipants({
                                      cohortId: cohort.id,
                                    }),
                                  );
                                  await onReload();
                                }, 'انتقال انجام نشد.')
                              }
                              className="rounded-lg border border-gray-300 px-2 py-1 text-[11px]"
                            >
                              {cohorts.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                      {!roster.length ? (
                        <tr>
                          <td colSpan={6} className="py-6 text-center text-xs font-bold text-gray-400">
                            این گروه هنوز عضوی ندارد.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>

                {!roster.length ? (
                  <button
                    type="button"
                    onClick={() =>
                      run(async () => {
                        if (!window.confirm('این گروه حذف شود؟')) return;
                        await portalAdminService.deleteCohort(cohort.id);
                        setOpenId('');
                        await onReload();
                      }, 'حذف گروه انجام نشد.')
                    }
                    className="flex items-center gap-1.5 text-[11px] font-bold text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    حذف گروه
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
